import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getProduct } from '../api'
import { useCart } from '../context/CartContext'
import { useTelegram } from '../context/TelegramContext'
import Header from '../components/Header'
import './ProductDetail.css'

const ProductDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedSize, setSelectedSize] = useState(null)
  const [selectedColor, setSelectedColor] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [quantity, setQuantity] = useState(1)

  const { addToCart, hapticFeedback } = useCart()
  const { showBackButton, hideBackButton, showAlert, showMainButton, hideMainButton } = useTelegram()

  useEffect(() => {
    loadProduct()

    // Настраиваем кнопку назад
    showBackButton(() => navigate('/'))

    return () => {
      hideBackButton()
      hideMainButton()
    }
  }, [id])

  const loadProduct = async () => {
    try {
      setLoading(true)
      const data = await getProduct(id)
      
      // Парсим sizes и colors если они строки
      if (data.sizes && typeof data.sizes === 'string') {
        try {
          data.sizes = JSON.parse(data.sizes)
        } catch (e) {
          data.sizes = []
        }
      }
      if (data.colors && typeof data.colors === 'string') {
        try {
          data.colors = JSON.parse(data.colors)
        } catch (e) {
          data.colors = []
        }
      }
      
      setProduct(data)
      
      // Автоматически выбираем первый размер и цвет если они есть
      if (data.sizes && data.sizes.length > 0) {
        setSelectedSize(data.sizes[0])
      }
      if (data.colors && data.colors.length > 0) {
        setSelectedColor(data.colors[0])
      }
      
      setError(null)
    } catch (err) {
      console.error('Ошибка загрузки товара:', err)
      setError('Не удалось загрузить товар')
    } finally {
      setLoading(false)
    }
  }

  const handleAddToCart = () => {
    if (!product) return

    // Проверяем наличие размера если они есть
    if (product.sizes && product.sizes.length > 0 && !selectedSize) {
      showAlert('Пожалуйста, выберите размер')
      return
    }

    // Проверяем наличие цвета если они есть
    if (product.colors && product.colors.length > 0 && !selectedColor) {
      showAlert('Пожалуйста, выберите цвет')
      return
    }

    addToCart(product, quantity, selectedSize, selectedColor)
    hapticFeedback('success')
    showAlert('Товар добавлен в корзину!')
  }

  const fixImagePath = (path) => {
    if (!path) return null
    const fixedPath = path.replace(/\\/g, '/')
    return fixedPath.startsWith('/') ? fixedPath : `/${fixedPath}`
  }

  const images = (() => {
    if (product?.image_urls) {
      try {
        const urls = typeof product.image_urls === 'string' ? JSON.parse(product.image_urls) : product.image_urls
        if (urls && Array.isArray(urls)) {
          return urls.map(url => fixImagePath(url)).filter(Boolean)
        }
      } catch (e) {
        return product?.image_url ? [fixImagePath(product.image_url)] : []
      }
    }
    return product?.image_url ? [fixImagePath(product.image_url)] : []
  })()

  if (loading) {
    return (
      <div className="product-detail-loading">
        <div className="spinner"></div>
        <p>Загрузка...</p>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="product-detail-error">
        <div className="error-icon">❌</div>
        <h3>Ошибка</h3>
        <p>{error || 'Товар не найден'}</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Вернуться в каталог
        </button>
      </div>
    )
  }

  return (
    <div className="product-detail">
      <Header title={product.name} showBack={true} />
      {/* Image Gallery */}
      <div className="image-gallery">
        {images.length > 0 ? (
          <>
            <div className="main-image">
              <img src={images[currentImageIndex]} alt={product.name} />
            </div>
            {images.length > 1 && (
              <div className="image-thumbnails">
                {images.map((img, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => {
                      setCurrentImageIndex(index)
                      hapticFeedback('light')
                    }}
                  >
                    <img src={img} alt={`${product.name} ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="image-placeholder">
            <span>📷</span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="product-content">
        <div className="product-header">
          <h1 className="product-title">{product.name}</h1>
          <div className="product-price">{product.price} ₽</div>
        </div>

        <p className="product-description">{product.description}</p>

        {/* Size Selection */}
        {product.sizes && product.sizes.length > 0 && (
          <div className="option-group">
            <label className="option-label">Размер</label>
            <div className="option-buttons">
              {product.sizes.map(size => (
                <button
                  key={size}
                  className={`option-button ${selectedSize === size ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedSize(size)
                    hapticFeedback('light')
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Color Selection */}
        {product.colors && product.colors.length > 0 && (
          <div className="option-group">
            <label className="option-label">Цвет</label>
            <div className="option-buttons">
              {product.colors.map(color => (
                <button
                  key={color}
                  className={`option-button ${selectedColor === color ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedColor(color)
                    hapticFeedback('light')
                  }}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quantity */}
        <div className="option-group">
          <label className="option-label">Количество</label>
          <div className="quantity-control">
            <button
              className="quantity-button"
              onClick={() => {
                if (quantity > 1) {
                  setQuantity(quantity - 1)
                  hapticFeedback('light')
                }
              }}
              disabled={quantity <= 1}
            >
              −
            </button>
            <span className="quantity-value">{quantity}</span>
            <button
              className="quantity-button"
              onClick={() => {
                setQuantity(quantity + 1)
                hapticFeedback('light')
              }}
            >
              +
            </button>
          </div>
        </div>

        {/* Add to Cart Button */}
        <button className="btn btn-primary btn-add-to-cart" onClick={handleAddToCart}>
          Добавить в корзину • {product.price * quantity} ₽
        </button>

        {/* Additional Info */}
        {product.category && (
          <div className="product-meta">
            <div className="meta-item">
              <span className="meta-label">Категория:</span>
              <span className="meta-value">{product.category}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductDetail


